import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  makeStyles,
  TextField,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import { i18n } from "../../translate/i18n";
import {
  listBoards,
  createCard,
  getClientes,
  listCardsByContact,
} from "../../services/kanban";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getPrimaryCliente } from "../TicketInfo";
import { requestFollowUpPrompt } from "../../helpers/followUpPrompt";
import ClienteQuickCreateModal from "../ClienteQuickCreateModal";
import CustomFieldInput, {
  normalizeFieldDefinitions,
  getFieldOptions,
  isFieldValueMissing,
} from "./CustomFieldInput";

// Campos do quadro que este modal já cobre com inputs próprios (título e
// descrição) ou preenche sozinho a partir do ticket — não entram na lista de
// campos personalizados renderizados abaixo dos seletores de quadro/coluna.
const HANDLED_FIELD_NAMES = new Set(["title", "description", "ticketId", "contactId"]);

const toDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().split("T")[0];
};

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: 400,
    paddingTop: theme.spacing(2),
  },
  formControl: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  duplicateAlert: {
    marginBottom: theme.spacing(2),
  },
  ticketInfo: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
}));

const CreateKanbanCardModal = ({ open, onClose, ticket, onCardCreated }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const sortByOrder = (items = []) =>
    [...items].sort((a, b) => (Number(a?.order) || 0) - (Number(b?.order) || 0));
  
  const [boards, setBoards] = useState([]);
  const [selectedBoardId, setSelectedBoardId] = useState("");
  const [selectedColumnId, setSelectedColumnId] = useState("");
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cardTitle, setCardTitle] = useState("");
  const [cardDescription, setCardDescription] = useState("");
  // A frase "Card criado a partir do ticket #X" só entra se o usuário marcar;
  // antes ela vinha sempre e virava a descrição de quase todos os cards.
  const [includeDefaultDescription, setIncludeDefaultDescription] = useState(false);
  const [descriptionError, setDescriptionError] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [loadingClientes, setLoadingClientes] = useState(false);
  const [clienteSearchText, setClienteSearchText] = useState("");
  const [quickCreateOpen, setQuickCreateOpen] = useState(false);

  // Card já existente do contato no escopo configurado pelo admin
  // (Configurações > Kanban): mode "warn" avisa e deixa criar, "blocked" impede
  const [duplicateCheck, setDuplicateCheck] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);

  // Valores dos campos personalizados do quadro escolhido e quais falharam na
  // validação de obrigatórios
  const [customValues, setCustomValues] = useState({});
  const [customErrors, setCustomErrors] = useState({});

  // Criar o card pode fechar o ticket e desmontar esta árvore antes das
  // requisições terminarem — sem isso o React avisa de setState em componente
  // desmontado
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // A busca de clientes roda em debounce e precisa saber quem está selecionado
  // sem virar dependência do efeito (senão o debounce reinicia a cada seleção)
  const selectedClienteRef = useRef(null);
  useEffect(() => {
    selectedClienteRef.current = selectedCliente;
  }, [selectedCliente]);

  useEffect(() => {
    if (open) {
      loadBoards();
      if (ticket) {
        setCardTitle(ticket.contact?.name || `Ticket #${ticket.id}`);
        setCardDescription("");
        setIncludeDefaultDescription(false);
        setDescriptionError(false);
      }
    }
  }, [open, ticket?.id]);

  // Pré-seleciona o cliente já vinculado ao contato do ticket.
  // O ticket vindo da conversa (ShowTicketService) já traz contactClientes; o da
  // lista de tickets não traz, então nesse caminho buscamos o contato.
  useEffect(() => {
    if (!open) return;

    // O cliente do vínculo vem com poucos campos (CODCLI/NOMECLI/NOMEFANTASIA) e
    // pode não estar no resultado da busca — garantir que ele exista nas opções
    const preselect = (cliente) => {
      setSelectedCliente(cliente);
      if (!cliente) return;
      setClientes((current) =>
        current.find((c) => c.CODCLI === cliente.CODCLI)
          ? current
          : [cliente, ...current]
      );
    };

    const contact = ticket?.contact;
    if (!contact?.id || contact.isGroup) {
      setSelectedCliente(null);
      return;
    }

    if (contact.contactClientes) {
      preselect(getPrimaryCliente(contact));
      return;
    }

    let active = true;
    setSelectedCliente(null);
    (async () => {
      try {
        const { data } = await api.get(`/contacts/${contact.id}`);
        if (active) {
          preselect(getPrimaryCliente(data));
        }
      } catch (error) {
        // Sem vínculo pré-selecionado o card é criado normalmente
        console.error("Erro ao buscar cliente vinculado ao contato:", error);
      }
    })();

    return () => {
      active = false;
    };
  }, [open, ticket?.contact?.id]);

  // Busca de clientes no servidor (mesmo padrão do CardModal do quadro)
  useEffect(() => {
    if (!open) return;

    const loadClientes = async () => {
      try {
        setLoadingClientes(true);
        const { data } = await getClientes({
          searchParam: clienteSearchText,
          pageNumber: 1,
        });
        if (!isMountedRef.current) return;

        const newClientes = data.clientes || [];

        // Mantém o cliente selecionado na lista mesmo quando ele não vem no
        // resultado da busca — senão o Autocomplete perde a opção pré-selecionada
        const current = selectedClienteRef.current;
        if (current && !newClientes.find((c) => c.CODCLI === current.CODCLI)) {
          setClientes([current, ...newClientes]);
        } else {
          setClientes(newClientes);
        }
      } catch (error) {
        console.error("Erro ao carregar clientes:", error);
      } finally {
        if (isMountedRef.current) setLoadingClientes(false);
      }
    };

    const timeoutId = setTimeout(loadClientes, 300);
    return () => clearTimeout(timeoutId);
  }, [clienteSearchText, open]);

  useEffect(() => {
    if (selectedBoardId) {
      const selectedBoard = boards.find(b => b.id === selectedBoardId);
      const sortedColumns = sortByOrder(selectedBoard?.columns || []);
      setColumns(sortedColumns);

      if (!sortedColumns.length) {
        setSelectedColumnId("");
        return;
      }

      setSelectedColumnId((currentColumnId) => {
        const hasCurrentColumn = sortedColumns.some(
          (column) => column.id === currentColumnId
        );
        return hasCurrentColumn ? currentColumnId : sortedColumns[0].id;
      });
    } else {
      setColumns([]);
      setSelectedColumnId("");
    }
  }, [selectedBoardId, boards]);

  // Verifica se o contato já tem card no quadro/coluna escolhidos. Quem decide o
  // escopo da busca (mesma coluna, mesmo quadro ou qualquer quadro) e o nível
  // (avisar/bloquear) é o backend, conforme a configuração da empresa — aqui só
  // reagimos ao que ele responde.
  useEffect(() => {
    const contactId = ticket?.contact?.id;

    setDuplicateCheck(null);

    if (
      !open ||
      !contactId ||
      ticket?.contact?.isGroup ||
      !selectedBoardId ||
      !selectedColumnId
    ) {
      // Uma consulta anterior pode ter sido descartada no meio do caminho
      setCheckingDuplicate(false);
      return;
    }

    let active = true;
    setCheckingDuplicate(true);

    (async () => {
      try {
        const { data } = await listCardsByContact(contactId, {
          boardId: selectedBoardId,
          columnId: selectedColumnId,
        });
        if (!active || !isMountedRef.current) return;

        const [card] = data?.cards || [];
        setDuplicateCheck(card ? { mode: data.mode, card } : null);
      } catch (error) {
        // Sem a pré-checagem o backend ainda barra na hora de criar
        console.error("Erro ao verificar card existente do contato:", error);
      } finally {
        if (active && isMountedRef.current) setCheckingDuplicate(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    open,
    ticket?.contact?.id,
    ticket?.contact?.isGroup,
    selectedBoardId,
    selectedColumnId,
  ]);

  // Definições de campo do quadro escolhido. A listagem de quadros já traz
  // fieldDefinitions, então não há chamada extra.
  const selectedBoard = boards.find((b) => b.id === selectedBoardId);
  const fieldDefinitions = useMemo(
    () => normalizeFieldDefinitions(selectedBoard?.fieldDefinitions),
    [selectedBoard]
  );

  // Mesmo recorte do modal do quadro: campos sempre visíveis, mais os
  // obrigatórios (que precisam aparecer para poderem ser preenchidos). Antes o
  // card era criado só com título/descrição e os obrigatórios do quadro
  // passavam em branco.
  const boardFields = useMemo(
    () =>
      fieldDefinitions.filter(
        (field) =>
          !HANDLED_FIELD_NAMES.has(field.fieldName) &&
          (field.alwaysVisible !== false || field.isRequired)
      ),
    [fieldDefinitions]
  );

  // Ao trocar de quadro, o formulário volta aos valores padrão de cada campo
  useEffect(() => {
    const defaults = {};

    fieldDefinitions.forEach((field) => {
      if (HANDLED_FIELD_NAMES.has(field.fieldName)) return;

      if (field.fieldType === "multiselect" || field.fieldType === "checkbox") {
        defaults[field.fieldName] = Array.isArray(field.defaultValue)
          ? field.defaultValue
          : [];
      } else if (field.fieldType === "date") {
        defaults[field.fieldName] = toDateInputValue(field.defaultValue);
      } else {
        defaults[field.fieldName] = field.defaultValue || "";
      }

      // Responsável (userID): nasce com o usuário logado, como no modal do quadro
      if (field.fieldName === "userID" && user?.id !== undefined && user?.id !== null) {
        const matched = getFieldOptions(field).find(
          (option) => String(option.value) === String(user.id)
        );
        defaults[field.fieldName] = matched ? matched.value : "";
      }
    });

    setCustomValues(defaults);
    setCustomErrors({});
  }, [fieldDefinitions, user?.id]);

  const handleCustomFieldChange = (fieldName, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldName]: value }));
    setCustomErrors((prev) =>
      prev[fieldName] ? { ...prev, [fieldName]: false } : prev
    );
  };

  const loadBoards = async () => {
    setLoading(true);
    try {
      // summary: colunas e campos sem os cards (o modal não lê cards da lista)
      const { data } = await listBoards({ isActive: true, summary: true });
      if (!isMountedRef.current) return;

      const sortedBoards = sortByOrder(data.boards || []);
      setBoards(sortedBoards);

      if (sortedBoards.length) {
        setSelectedBoardId(sortedBoards[0].id);
      } else {
        setSelectedBoardId("");
      }
    } catch (error) {
      console.error("Erro ao carregar boards:", error);
      toast.error(i18n.t("createKanbanCardModal.toasts.loadBoardsError"));
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const defaultDescription = ticket
    ? `Card criado a partir do ticket #${ticket.id}`
    : "";

  const handleCreateCard = async () => {
    if (!selectedBoardId || !selectedColumnId) {
      toast.warning(i18n.t("createKanbanCardModal.toasts.selectBoardAndColumn"));
      return;
    }

    if (!ticket) {
      toast.error(i18n.t("createKanbanCardModal.toasts.ticketNotFound"));
      return;
    }

    if (duplicateCheck?.mode === "blocked") {
      return;
    }

    const finalDescription = [
      cardDescription.trim(),
      includeDefaultDescription ? defaultDescription : "",
    ]
      .filter(Boolean)
      .join("\n");

    // Descrição fica fora de boardFields (tem input próprio), mas o quadro pode
    // marcá-la como obrigatória
    const descriptionRequired = fieldDefinitions.some(
      (field) => field.fieldName === "description" && field.isRequired
    );
    if (descriptionRequired && !finalDescription) {
      setDescriptionError(true);
      toast.error(
        i18n.t("createKanbanCardModal.toasts.requiredFieldsMissing", {
          fields: i18n.t("common.description"),
        })
      );
      return;
    }

    // Obrigatórios do quadro: mesma validação do modal do quadro. O backend
    // também barra, mas aqui o usuário vê qual campo faltou.
    const missingFields = boardFields.filter(
      (field) => field.isRequired && isFieldValueMissing(customValues[field.fieldName])
    );
    if (missingFields.length > 0) {
      setCustomErrors(
        missingFields.reduce((acc, field) => ({ ...acc, [field.fieldName]: true }), {})
      );
      toast.error(
        i18n.t("createKanbanCardModal.toasts.requiredFieldsMissing", {
          fields: missingFields.map((field) => field.fieldLabel).join(", "),
        })
      );
      return;
    }

    setSubmitting(true);
    try {
      const finalTitle = cardTitle || ticket.contact?.name || `Ticket #${ticket.id}`;
      const hasBoardField = (fieldName) =>
        fieldDefinitions.some((field) => field.fieldName === fieldName);

      // customFields no mesmo formato que o modal do quadro grava (título e
      // descrição incluídos), mais os campos que vêm do ticket
      const customFields = {
        ...customValues,
        title: finalTitle,
        description: finalDescription,
        priority: "media",
      };
      if (hasBoardField("ticketId")) {
        customFields.ticketId = ticket.id;
      }
      if (hasBoardField("contactId") && ticket.contact?.id) {
        customFields.contactId = ticket.contact.id;
      }
      // Sem campo userID no quadro, mantém o id do usuário logado (comportamento
      // antigo deste modal); com o campo, o valor já veio de customValues.
      if (!hasBoardField("userID") && user && user.id) {
        customFields.userID = user.id;
      }

      const cardData = {
        boardId: selectedBoardId,
        columnId: selectedColumnId,
        ticketId: ticket.id,
        contactId: ticket.contact?.id,
        clienteId: selectedCliente?.CODCLI || null,
        title: finalTitle,
        description: finalDescription,
        priority: "media",
        customFields,
        // O aviso de card duplicado já foi exibido antes deste clique; o backend
        // exige o flag para deixar passar no modo "avisar"
        ...(duplicateCheck?.mode === "warn"
          ? { confirmedDuplicateContact: true }
          : {}),
      };

      await createCard(cardData);
      toast.success(i18n.t("createKanbanCardModal.toasts.cardCreated"));
      if (onCardCreated) {
        await onCardCreated();
      }
      handleClose();

      // Leva cliente e contato do ticket para o follow-up já nascer preenchido.
      // O contato só vai junto com o cliente — a lista de contatos do modal é
      // carregada a partir do cliente escolhido.
      // Sem await de propósito: este prompt resolve por callback (CustomEvent) e
      // pode nunca resolver. Dentro do try, o finally abaixo não rodaria e o botão
      // ficaria em spinner para sempre nas instâncias do modal que não desmontam.
      requestFollowUpPrompt(i18n.t("createKanbanCardModal.followUpSource"), {
        cliente: selectedCliente || null,
        clienteId: selectedCliente?.CODCLI || null,
        contatoId: selectedCliente ? ticket.contact?.id || null : null,
        // Mesmo shape legado da lista /clientes/:id/contatos (id = Contacts.id):
        // o campo já mostra o nome enquanto a lista do cliente não chega
        contato:
          selectedCliente && ticket.contact?.id
            ? { id: ticket.contact.id, nome: ticket.contact.name || "" }
            : null,
      }).catch((err) => {
        console.error("Erro no follow-up depois de criar o card:", err);
      });
    } catch (error) {
      console.error("Erro ao criar card:", error);

      // Card duplicado que a pré-checagem não pegou (criado em paralelo, ou modal
      // aberto antes da configuração mudar): mostra o aviso e, no modo "avisar",
      // o próximo clique já vai confirmado
      const errorCode = error.response?.data?.error;
      if (
        errorCode === "ERR_KANBAN_CARD_DUPLICATE_CONTACT" ||
        errorCode === "ERR_KANBAN_CARD_DUPLICATE_CONTACT_BLOCKED"
      ) {
        const mode =
          errorCode === "ERR_KANBAN_CARD_DUPLICATE_CONTACT_BLOCKED"
            ? "blocked"
            : "warn";
        setDuplicateCheck((current) => ({ mode, card: current?.card || null }));
      }

      toastError(error);
    } finally {
      if (isMountedRef.current) setSubmitting(false);
    }
  };

  // O cadastro rápido não se fecha sozinho no sucesso — quem fecha é este
  // handler. Como a busca de clientes é disparada pelo onChange do TextField
  // (e não pelo onInputChange), selecionar aqui não reinicia o debounce e a
  // injeção nas options sobrevive
  const handleClienteCreated = (novoCliente) => {
    setQuickCreateOpen(false);
    if (!novoCliente?.CODCLI) return;

    setClientes((current) =>
      current.some((c) => String(c.CODCLI) === String(novoCliente.CODCLI))
        ? current
        : [novoCliente, ...current]
    );
    setSelectedCliente(novoCliente);
  };

  const handleClose = () => {
    // Fechar depois de criar o card pode acontecer com a árvore já desmontada
    // (o ticket foi encerrado); só o onClose precisa rodar sempre
    if (isMountedRef.current) {
      setSelectedBoardId("");
      setSelectedColumnId("");
      setColumns([]);
      setCardTitle("");
      setCardDescription("");
      setIncludeDefaultDescription(false);
      setDescriptionError(false);
      setSelectedCliente(null);
      setClienteSearchText("");
      setClientes([]);
      setDuplicateCheck(null);
      setQuickCreateOpen(false);
      setCustomValues({});
      setCustomErrors({});
      // Sem isto, uma instância que não desmonta reabre com o botão em spinner
      setSubmitting(false);
    }
    onClose();
  };

  const duplicateCard = duplicateCheck?.card;
  const duplicateMessage = duplicateCard
    ? i18n.t("createKanbanCardModal.duplicate.withDetails", {
        title: duplicateCard.title || i18n.t("createKanbanCardModal.duplicate.untitled"),
        id: duplicateCard.id,
        board: duplicateCard.boardName,
        column: duplicateCard.columnName,
        createdBy:
          duplicateCard.createdByName && duplicateCard.createdByName !== "-"
            ? i18n.t("createKanbanCardModal.duplicate.createdBy", {
                name: duplicateCard.createdByName,
              })
            : "",
      })
    : i18n.t("createKanbanCardModal.duplicate.simple");

  return (
    <>
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("ticketActionButtons.tooltips.createKanbanCard")}</DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {loading ? (
          <div className={classes.loadingContainer}>
            <CircularProgress />
          </div>
        ) : (
          <>
            {duplicateCheck && (
              <Alert
                severity={duplicateCheck.mode === "blocked" ? "error" : "warning"}
                className={classes.duplicateAlert}
              >
                {duplicateMessage}{" "}
                {duplicateCheck.mode === "blocked"
                  ? i18n.t("createKanbanCardModal.duplicate.blockedHint")
                  : i18n.t("createKanbanCardModal.duplicate.warnHint")}
              </Alert>
            )}

            {ticket && (
              <div className={classes.ticketInfo}>
                <Typography variant="subtitle2" gutterBottom>
                  <strong>{i18n.t("createKanbanCardModal.info.ticket")}</strong> #{ticket.id}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>{i18n.t("createKanbanCardModal.info.contact")}</strong> {ticket.contact?.name || i18n.t("createKanbanCardModal.info.noName")}
                </Typography>
                {ticket.contact?.number && (
                  <Typography variant="body2" color="textSecondary">
                    <strong>{i18n.t("createKanbanCardModal.info.number")}</strong> {ticket.contact.number}
                  </Typography>
                )}
              </div>
            )}

            <TextField
              label={i18n.t("createKanbanCardModal.form.title")}
              fullWidth
              value={cardTitle}
              onChange={(e) => setCardTitle(e.target.value)}
              className={classes.formControl}
              variant="outlined"
              disabled={submitting}
            />

            <Box className={classes.formControl}>
              <TextField
                label={i18n.t("createKanbanCardModal.form.description")}
                placeholder={i18n.t("common.optional")}
                fullWidth
                multiline
                rows={3}
                value={cardDescription}
                onChange={(e) => {
                  setCardDescription(e.target.value);
                  setDescriptionError(false);
                }}
                variant="outlined"
                disabled={submitting}
                error={descriptionError}
                helperText={descriptionError ? i18n.t("createKanbanCardModal.form.descriptionRequired") : ""}
              />
              {ticket && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      size="small"
                      checked={includeDefaultDescription}
                      onChange={(e) => {
                        setIncludeDefaultDescription(e.target.checked);
                        setDescriptionError(false);
                      }}
                      disabled={submitting}
                    />
                  }
                  label={
                    <Typography variant="body2" color="textSecondary">
                      {i18n.t("createKanbanCardModal.form.addDefaultDescription", { description: defaultDescription })}
                    </Typography>
                  }
                />
              )}
            </Box>

            <Box
              display="flex"
              alignItems="flex-start"
              className={classes.formControl}
              style={{ gap: 8 }}
            >
              <Box flex={1}>
                <Autocomplete
                  options={clientes}
                  filterOptions={(options) => options}
                  getOptionSelected={(option, value) => option.CODCLI === value?.CODCLI}
                  getOptionLabel={(option) => {
                    const nome = option.NOMEFANTASIA || option.NOMECLI || "";
                    const cidade = option.CIDADECLI
                      ? ` - ${option.CIDADECLI}/${option.ESTADOCLI}`
                      : "";
                    return `${nome}${cidade}`;
                  }}
                  value={selectedCliente}
                  onChange={(event, newValue) => setSelectedCliente(newValue)}
                  loading={loadingClientes}
                  disabled={submitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={i18n.t("createKanbanCardModal.form.client")}
                      variant="outlined"
                      placeholder={i18n.t("createKanbanCardModal.form.clientPlaceholder")}
                      // Busca no onChange do input (e não no onInputChange do
                      // Autocomplete) para a pré-seleção não disparar nova busca
                      onChange={(e) => setClienteSearchText(e.target.value || "")}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingClientes ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(option) => (
                    <Box>
                      <Typography variant="body2">
                        <strong>{option.NOMEFANTASIA || option.NOMECLI}</strong>
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {option.NOMECLI}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {option.CGCCLI || option.CPFCLI}
                        {option.CIDADECLI ? ` · ${option.CIDADECLI}/${option.ESTADOCLI}` : ""}
                      </Typography>
                    </Box>
                  )}
                  noOptionsText={i18n.t("createKanbanCardModal.form.clientNotFound")}
                />
              </Box>
              <Tooltip title={i18n.t("createKanbanCardModal.form.newClient")}>
                {/* span: o Tooltip do MUI v4 não escuta eventos de botão desabilitado */}
                <span>
                  <IconButton
                    color="primary"
                    onClick={() => setQuickCreateOpen(true)}
                    disabled={submitting}
                    style={{ marginTop: 8 }}
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <FormControl className={classes.formControl}>
              <InputLabel id="board-select-label">{i18n.t("createKanbanCardModal.form.board")}</InputLabel>
              <Select
                labelId="board-select-label"
                value={selectedBoardId}
                onChange={(e) => setSelectedBoardId(e.target.value)}
                disabled={submitting}
              >
                <MenuItem value="">
                  <em>{i18n.t("createKanbanCardModal.form.selectBoard")}</em>
                </MenuItem>
                {boards.map((board) => (
                  <MenuItem key={board.id} value={board.id}>
                    {board.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className={classes.formControl} disabled={!selectedBoardId}>
              <InputLabel id="column-select-label">{i18n.t("createKanbanCardModal.form.column")}</InputLabel>
              <Select
                labelId="column-select-label"
                value={selectedColumnId}
                onChange={(e) => setSelectedColumnId(e.target.value)}
                disabled={!selectedBoardId || submitting}
              >
                <MenuItem value="">
                  <em>{i18n.t("createKanbanCardModal.form.selectColumn")}</em>
                </MenuItem>
                {columns.map((column) => (
                  <MenuItem key={column.id} value={column.id}>
                    {column.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {boardFields.length > 0 && (
              <Box className={classes.formControl}>
                <Typography variant="subtitle2" gutterBottom>
                  {i18n.t("createKanbanCardModal.form.boardFields")}
                </Typography>
                {boardFields.map((field) => (
                  <CustomFieldInput
                    key={field.id || field.fieldName}
                    field={field}
                    value={customValues[field.fieldName]}
                    onChange={handleCustomFieldChange}
                    error={Boolean(customErrors[field.fieldName])}
                    disabled={submitting}
                  />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary" disabled={submitting}>
          {i18n.t("common.cancel")}
        </Button>
        <Button
          onClick={handleCreateCard}
          color="primary"
          variant="contained"
          disabled={
            !selectedBoardId ||
            !selectedColumnId ||
            submitting ||
            checkingDuplicate ||
            duplicateCheck?.mode === "blocked"
          }
        >
          {submitting ? <CircularProgress size={24} /> : i18n.t("ticketActionButtons.createCard")}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Irmão do Dialog: dentro do DialogContent o ESC do cadastro rápido
        fecharia também o modal do card */}
    <ClienteQuickCreateModal
      open={quickCreateOpen}
      onClose={() => setQuickCreateOpen(false)}
      onSave={handleClienteCreated}
      initialName={clienteSearchText}
    />
    </>
  );
};

export default CreateKanbanCardModal;
